import React, { createContext, useCallback, useMemo, useRef, useState } from "react";

import { computeAffineTransform, computeImageCorners, computeResidualError } from "./affineTransform";
import { GCP, ImageOverlayContextValue, ImageOverlayMode, ImageOverlayState } from "./types";

const initialState: ImageOverlayState = {
  imageName: null,
  imageUrl: null,
  imageWidth: 0,
  imageHeight: 0,
  gcps: [],
  activeGcpId: null,
  mode: "idle",
  opacity: 0.7,
  isVisible: true,
  transformMatrix: null,
};

export const ImageOverlayContext = createContext<ImageOverlayContextValue | null>(null);

export const ImageOverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ImageOverlayState>(initialState);
  const loadGeneration = useRef(0);

  const loadImage = useCallback((file: File) => {
    // Increment generation so any in-flight async loads from previous calls are ignored
    loadGeneration.current += 1;
    const generation = loadGeneration.current;

    // Reset all state immediately so stale data doesn't linger during async loading
    setState(initialState);

    // Strip the file extension to get a default image name
    const imageName = file.name.replace(/\.[^.]+$/, "");

    const reader = new FileReader();
    reader.onload = (e) => {
      if (generation !== loadGeneration.current) return;
      const dataUrl = e.target?.result as string;
      const img = new Image();
      img.onload = () => {
        if (generation !== loadGeneration.current) return;
        setState({
          ...initialState,
          imageName,
          imageUrl: dataUrl,
          imageWidth: img.naturalWidth,
          imageHeight: img.naturalHeight,
        });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }, []);

  const addGcp = useCallback(() => {
    setState((prev) => {
      const id = `gcp-${Date.now()}`;
      const maxColorIndex = prev.gcps.length > 0 ? Math.max(...prev.gcps.map((gcp) => gcp.colorIndex)) : -1;
      const newGcp: GCP = { id, imageX: 0, imageY: 0, mapLng: null, mapLat: null, colorIndex: maxColorIndex + 1 };
      return {
        ...prev,
        gcps: [...prev.gcps, newGcp],
        activeGcpId: id,
        mode: "place-image" as ImageOverlayMode,
      };
    });
  }, []);

  const removeGcp = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      gcps: prev.gcps.filter((gcp) => gcp.id !== id),
      activeGcpId: prev.activeGcpId === id ? null : prev.activeGcpId,
      mode: prev.activeGcpId === id ? "idle" : prev.mode,
    }));
  }, []);

  const setImagePoint = useCallback((gcpId: string, x: number, y: number) => {
    setState((prev) => ({
      ...prev,
      gcps: prev.gcps.map((gcp) => (gcp.id === gcpId ? { ...gcp, imageX: x, imageY: y } : gcp)),
      mode: "place-map",
    }));
  }, []);

  const setMapPoint = useCallback((gcpId: string, lng: number, lat: number) => {
    setState((prev) => ({
      ...prev,
      gcps: prev.gcps.map((gcp) => (gcp.id === gcpId ? { ...gcp, mapLng: lng, mapLat: lat } : gcp)),
      activeGcpId: null,
      mode: "idle",
    }));
  }, []);

  const startPlacingImage = useCallback((gcpId: string) => {
    setState((prev) => ({ ...prev, activeGcpId: gcpId, mode: "place-image" }));
  }, []);

  const startPlacingMap = useCallback((gcpId: string) => {
    setState((prev) => ({ ...prev, activeGcpId: gcpId, mode: "place-map" }));
  }, []);

  const cancelPlacing = useCallback(() => {
    setState((prev) => ({ ...prev, activeGcpId: null, mode: "idle" }));
  }, []);

  const setOpacity = useCallback((opacity: number) => {
    setState((prev) => ({ ...prev, opacity }));
  }, []);

  const setVisible = useCallback((isVisible: boolean) => {
    setState((prev) => ({ ...prev, isVisible }));
  }, []);

  const clearAll = useCallback(() => {
    setState((prev) => ({ ...prev, gcps: [], activeGcpId: null, mode: "idle" }));
  }, []);

  // Compute the affine transform whenever GCPs change
  const completeGcps = useMemo(
    () => state.gcps.filter((gcp): gcp is GCP & { mapLng: number; mapLat: number } => gcp.mapLng != null && gcp.mapLat != null),
    [state.gcps]
  );

  const completeGcpCount = completeGcps.length;

  const transformMatrix = useMemo(() => {
    if (completeGcps.length < 3) return null;
    return computeAffineTransform(
      completeGcps.map((gcp) => ({
        imageX: gcp.imageX,
        imageY: gcp.imageY,
        mapLng: gcp.mapLng,
        mapLat: gcp.mapLat,
      }))
    );
  }, [completeGcps]);

  const imageCorners = useMemo(() => {
    if (!transformMatrix || !state.imageWidth || !state.imageHeight) return null;
    return computeImageCorners(transformMatrix, state.imageWidth, state.imageHeight);
  }, [transformMatrix, state.imageWidth, state.imageHeight]);

  const residualError = useMemo(() => {
    if (!transformMatrix || completeGcps.length === 0) return null;
    return computeResidualError(
      transformMatrix,
      completeGcps.map((gcp) => ({
        imageX: gcp.imageX,
        imageY: gcp.imageY,
        mapLng: gcp.mapLng,
        mapLat: gcp.mapLat,
      }))
    );
  }, [transformMatrix, completeGcps]);

  const value: ImageOverlayContextValue = useMemo(
    () => ({
      ...state,
      transformMatrix,
      imageCorners,
      completeGcpCount,
      residualError,
      loadImage,
      addGcp,
      removeGcp,
      setImagePoint,
      setMapPoint,
      startPlacingImage,
      startPlacingMap,
      cancelPlacing,
      setOpacity,
      setVisible,
      clearAll,
    }),
    [
      state,
      transformMatrix,
      imageCorners,
      completeGcpCount,
      residualError,
      loadImage,
      addGcp,
      removeGcp,
      setImagePoint,
      setMapPoint,
      startPlacingImage,
      startPlacingMap,
      cancelPlacing,
      setOpacity,
      setVisible,
      clearAll,
    ]
  );

  return <ImageOverlayContext.Provider value={value}>{children}</ImageOverlayContext.Provider>;
};
